import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentUser } from "../../../utils/auth";
import { Plus, Eye, Calendar, Users, Award } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { AppButton, AppBadge, EmptyState } from "../../ui";

const statusVariant = { published: "primary", closed: "secondary" };

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

/**
 * BatchGeneralAssignments — Embedded view of general assignments for a specific batch.
 * Used inside BatchProfile as a tab.
 */
const BatchGeneralAssignments = ({ batchId }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!batchId) return;
    apiClient.get("/general-assignments/list", { params: { batchId } })
      .then(({ data }) => setAssignments(data.assignments || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [batchId]);

  if (loading) return <div className="text-center py-4 text-muted">Loading...</div>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted" style={{ fontSize: "0.875rem" }}>
          {assignments.length} assignment{assignments.length !== 1 ? "s" : ""}
        </span>
        <AppButton size="sm" onClick={() => navigate(getCurrentUser()?.usertype === "instructor" ? "/i/batch-assignments/create" : "/batch-assignments/create")}>
          <Plus size={14} /> New Assignment
        </AppButton>
      </div>

      {assignments.length === 0 ? (
        <EmptyState
          icon={<Award size={36} />}
          title="No assignments"
          description="Create a general assignment for this batch"
        />
      ) : (
        <div className="ga-list__table-wrap">
          <table className="ga-list__table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Due Date</th>
                <th>Max Marks</th>
                <th>Submissions</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a._id}>
                  <td className="ga-list__title-cell">{a.title}</td>
                  <td><Calendar size={13} /> {formatDate(a.dueDate)}</td>
                  <td>{a.maxMarks}</td>
                  <td>
                    <Users size={13} /> {a.submissionCount || 0}
                    {a.gradedCount > 0 && ` (${a.gradedCount} graded)`}
                  </td>
                  <td>
                    <AppBadge variant="outline" color={statusVariant[a.status]}>{a.status}</AppBadge>
                  </td>
                  <td>
                    <button
                      className="ga-list__action-btn"
                      title="View"
                      onClick={() => navigate(`${getCurrentUser()?.usertype === "instructor" ? "/i/batch-assignments" : "/batch-assignments"}/${a._id}`)}
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BatchGeneralAssignments;
